import React, { useState, useEffect } from 'react';
import { Grid } from 'components/mui';
import { CaseFormData, CaseValidation } from 'containers/pages/cases/types';
import searchCustomers, { CustomerOption } from 'api/cases/searchCustomers';
import searchConsultants, { ConsultantOption } from 'api/cases/searchConsultants';
import searchContacts, { ContactOption } from 'api/cases/searchContacts';
import FormAutocomplete, { ensureOption } from 'containers/pages/cases/components/caseForm/FormAutocomplete';
import FormSection from 'containers/pages/cases/components/caseForm/FormSection';

interface AssignmentSectionProps {
	form: CaseFormData;
	valid: CaseValidation;
	mode: 'add' | 'edit' | 'copy';
	onCustomerChange: (_event: unknown, value: CustomerOption | null) => void;
	onConsultantChange: (_event: unknown, value: ConsultantOption | null) => void;
	onContactChange: (_event: unknown, value: ContactOption | null) => void;
}

const AssignmentSection: React.FC<AssignmentSectionProps> = ({
	form,
	valid,
	mode,
	onCustomerChange,
	onConsultantChange,
	onContactChange,
}) => {
	const [customers, setCustomers] = useState<CustomerOption[]>([]);
	const [consultants, setConsultants] = useState<ConsultantOption[]>([]);
	const [contacts, setContacts] = useState<ContactOption[]>([]);

	useEffect(() => {
		searchCustomers('').then(setCustomers).catch(() => {});
		searchConsultants('').then(setConsultants).catch(() => {});
	}, []);

	useEffect(() => {
		if (form.CustomerId) {
			searchContacts(form.CustomerId, '').then(setContacts).catch(() => {});
		}
	}, [form.CustomerId]);

	const isCustomerLocked = mode === 'edit';
	const customerValue: CustomerOption | null = form.CustomerId
		? { Id: form.CustomerId, Name: form.CustomerName }
		: null;
	const consultantValue: ConsultantOption | null = form.ConsultantId
		? { Id: form.ConsultantId, FullName: form.ConsultantName }
		: null;
	const contactValue: ContactOption | null = form.PrimaryContactId
		? { Id: form.PrimaryContactId, FullName: form.PrimaryContactName }
		: null;

	const customerOptions = ensureOption(customers, customerValue);
	const consultantOptions = ensureOption(consultants, consultantValue);
	const contactOptions = ensureOption(contacts, contactValue);

	return (
		<FormSection title="Assignment">
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<FormAutocomplete
						label="Customer"
						required
						options={customerOptions}
						value={customerValue}
						onChange={onCustomerChange}
						getOptionLabel={(opt) => opt.Name}
						onSearch={(val) => searchCustomers(val).then(setCustomers).catch(() => {})}
						disabled={isCustomerLocked}
						error={valid.CustomerId.isErr}
						helperText={isCustomerLocked ? 'Locked in edit mode' : valid.CustomerId.errMsg}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormAutocomplete
						label="Consultant"
						required
						options={consultantOptions}
						value={consultantValue}
						onChange={onConsultantChange}
						getOptionLabel={(opt) => opt.FullName}
						onSearch={(val) => searchConsultants(val).then(setConsultants).catch(() => {})}
						error={valid.ConsultantId.isErr}
						helperText={valid.ConsultantId.errMsg}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormAutocomplete
						label="Primary Contact"
						options={contactOptions}
						value={contactValue}
						onChange={onContactChange}
						getOptionLabel={(opt) => opt.FullName}
						onSearch={(val) => {
							if (form.CustomerId) searchContacts(form.CustomerId, val).then(setContacts).catch(() => {});
						}}
						disabled={!form.CustomerId}
						helperText={!form.CustomerId ? 'Select a customer first' : ''}
					/>
				</Grid>
			</Grid>
		</FormSection>
	);
};

export default AssignmentSection;
